import { NavbarStateService } from '@app/core/services/navbar-state.service';
import { Title, TitleService } from '@app/core/services/title.service';
import { SessionService } from '@app/security/session.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Client } from '@app/core/models/client';
import { ClientService } from '@app/core/services/client.service';
import { Ordering } from '@app/core/utils/ordering';
import { Subscription, isObservable } from 'rxjs';
import { take } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { UnsavedChangesService } from '@app/core/services/unsaved-changes.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { FeatureService } from '@app/core/services/feature.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [NgClass, FormsModule, SpinnerComponent, RouterLink]
})
export class HeaderComponent implements OnInit, OnDestroy {

  showUserProfile: boolean;
  loadingClientList: boolean;
  fetchedClients: boolean;
  clients: Client[];
  currentClient: Client;
  titleSub: Subscription;
  title: Title;
  searchTerm: string = '';
  featureFlags = FeatureFlags;
  isRMEnabled: boolean = false;
  private ignoreNextWindowClick = false;

  constructor(private router: Router,
    public navbarStateService: NavbarStateService,
    private clientService: ClientService,
    private titleService: TitleService,
    private ref: ChangeDetectorRef,
    public sessionService: SessionService,
    private unsavedChangesService: UnsavedChangesService,
    private featureService: FeatureService) { }

  ngOnInit() {
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.titleSub = this.titleService.titleChange.subscribe((title: Title) => {
      this.title = title;
      this.ref.detectChanges();
    });
    this.isRMEnabled = this.currentClient.two_way_config?.enabled;
  }

  ngOnDestroy() {
    this.titleSub.unsubscribe();
  }

  onButtonClick() {
    if (this.title.buttonHandler) {
      this.title.buttonHandler();
    }
  }

  switchClients(client: Client) {
    this.sessionService.setClient(client);
    this.currentClient = client;
    this.showUserProfile = false;

    if (this.isRMEnabled) {
      // clear url history state to prevent users from navigating back to a url with the previous client id for RM
      const basePath = '/two-way/relay-messenger';
      if (this.router.url.startsWith(basePath)) {
        window.location.replace(basePath);
        return;
      }
    }
    // hack to ensure the components actually reload on client change.  not ideal.
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(`safe-redirect?redirect=${location.pathname}`);
    });
  }

  toggleUserProfile(e) {
    if (this.isTranslationsEnabled) {
      this.guardUnsavedChanges(() => this.toggleUserProfileInternal(e));
      return;
    }
    this.toggleUserProfileInternal(e);
  }

  private toggleUserProfileInternal(e) {
    this.showUserProfile = !this.showUserProfile;
    if (!this.fetchedClients) {
      this.loadingClientList = true;
      this.fetchedClients = true;
      const ordering = new Ordering('data.company_name');
      this.clientService.getAllClients(ordering).subscribe((clients) => {
        this.clients = clients;
        this.loadingClientList = false;
      });
    }
    e?.stopPropagation();
  }

  private guardUnsavedChanges(action: () => void) {
    const result = this.unsavedChangesService.canNavigate();
    if (result === true) {
      action();
      return;
    }
    if (result === false) {
      return;
    }
    // result is an Observable that will emit true if navigation should proceed, or false if it should be cancelled. This allows the unsavedChangesService to show a confirmation dialog to the user and wait for their response before allowing navigation.
    if (isObservable(result)) {
      result.pipe(take(1)).subscribe((allow) => {
        if (allow) {
          this.ignoreNextWindowClick = true;
          this.unsavedChangesService.allowNextNavigationOnce();
          action();
        }
      });
    }
  }

  private get isTranslationsEnabled(): boolean {
    return (
      this.featureService.checkFlag(this.featureFlags.rm_multi_lingual) &&
      !!this.currentClient?.two_way_config?.translations_enabled
    );
  }

  hideUserProfile(event) {
    if (this.ignoreNextWindowClick) {
      this.ignoreNextWindowClick = false;
      return;
    }
    const userProfileMenuSearchInput: string = 'user-profile-menu-search-input';
    const userProfileMenuClearIcon: string = 'user-profile-menu-clear-icon';
    const target = event.target || event.srcElement || event.currentTarget;
    const elementId = target.attributes.id;
    const value = elementId?.nodeValue;

    if (value !== userProfileMenuSearchInput && value !== userProfileMenuClearIcon) {
      this.showUserProfile = false
      this.searchTerm = '';
    };
  }

  filteredClients() {
    if (!this.searchTerm) {
      return this.sessionService.allClients;
    }

    const normalizedSearchTerm = this.normalizedSearchTerm;

    return this.sessionService.allClients.filter((client) => {
      const clientName = client.company_name?.toLowerCase() ?? '';
      const clientId = client.id?.toLowerCase() ?? '';

      return clientName.includes(normalizedSearchTerm) || clientId.includes(normalizedSearchTerm);
    });
  }

  shouldDisplayClientIdForSearch(client: Client): boolean {
    if (!this.normalizedSearchTerm) {
      return false;
    }

    const clientName = client.company_name?.toLowerCase() ?? '';
    const clientId = client.id?.toLowerCase() ?? '';

    return clientId.includes(this.normalizedSearchTerm) && !clientName.includes(this.normalizedSearchTerm);
  }

  clearSearchBar() {
    this.searchTerm = '';
  }

  get hasNoClientsFromQueryDisplayError(): boolean {
    const filteredClients = this.filteredClients();
    return !this.loadingClientList && filteredClients.length === 0;
  }
  
  get hasClientsDisplaySearchBar(): boolean {
    return !this.loadingClientList && this.clients.length > 1;
  }

  private get normalizedSearchTerm(): string {
    return this.searchTerm.toLowerCase().trim();
  }
}
